import * as React from 'react';
import TranslateNew from '@translate/TranslateNew';
import { styleColors, typography } from 'src/main.style';

const tn: TranslateNew = TranslateNew.getInstance();

// Space the input must leave for the rest of the screen once the keyboard is up:
// the chat header, the bottom bar chrome and a glimpse of the conversation. On
// short screens (e.g. iPhone SE) this keeps the growing input from sliding under
// the keyboard; on tall screens the configured max (220px) wins, so nothing changes.
const VIEWPORT_CHROME_RESERVE_PX = 150;
const MIN_VIEWPORT_MAX_HEIGHT_PX = 80;

interface IVisualViewport {
  height:number;
  addEventListener:(type:"resize"|"scroll", listener:() => void) => void;
  removeEventListener:(type:"resize"|"scroll", listener:() => void) => void;
}

interface IProps {
  value:string;
  placeholder?:string;
  onChange:(evt: React.ChangeEvent<HTMLTextAreaElement>)=> void;
  onPaste?:(evt: React.ClipboardEvent<HTMLTextAreaElement>)=> void;
  handleKeyPress:(evt:React.KeyboardEvent<HTMLTextAreaElement>) => void
  style?: React.CSSProperties;
  readOnly?: boolean;
}

interface IState {
  viewportMaxHeight?: number;
}


class Autosize extends React.Component<IProps, IState> {

  public state: IState = {};

  private textAreaRef = React.createRef<HTMLTextAreaElement>();
  private lifecycleMounted:boolean = false;
  private visualViewport:IVisualViewport|undefined;
  private viewportUpdateFrame:number|undefined;


  public render() {

    const placeholder = this.props.placeholder ? this.props.placeholder : tn.lang.labelMessage;

    return (
      <textarea
        onChange={(evt) => this.onInternalChange(evt)}
        onKeyDown={(evt) => this.props.handleKeyPress(evt)}
        onPaste={this.props.onPaste}
        placeholder={placeholder}
        ref={this.textAreaRef}
        maxLength={5000}
        rows={1}
        value={this.props.value}
        readOnly={this.props.readOnly}
        autoCorrect="on"
        spellCheck={true}
        autoComplete="off"
        style={{
          flexGrow: 1,
          maxHeight:'220px',
          padding: '0px',
          margin: '3px',
          borderWidth: '0px',
          boxSizing: 'border-box',
          /* Inherit the surface behind us so the input adapts to the
             current theme — browser default is solid white. */
          backgroundColor: 'transparent',
          color: styleColors.greyDark2,
          ...typography.input,
          textAlign: 'left',
          width: 'calc(100% - 6px)',
          fontSize:'16px',
          lineHeight:'18px',
          overflowY: 'hidden',
          resize: 'none',
          ...this.props.style
        }}
      />
    );
  }

  public componentDidMount() {
    this.lifecycleMounted = true;
    this.visualViewport = this.getVisualViewport();
    this.safeSyncHeight("mount");
    this.scheduleViewportUpdate();
    window.addEventListener("resize", this.scheduleViewportUpdate);
    if (this.visualViewport) {
      this.visualViewport.addEventListener("resize", this.scheduleViewportUpdate);
      this.visualViewport.addEventListener("scroll", this.scheduleViewportUpdate);
    }
  }

  public componentWillUnmount() {
    this.lifecycleMounted = false;
    if (this.viewportUpdateFrame !== undefined) {
      window.cancelAnimationFrame(this.viewportUpdateFrame);
      this.viewportUpdateFrame = undefined;
    }
    window.removeEventListener("resize", this.scheduleViewportUpdate);
    if (this.visualViewport) {
      this.visualViewport.removeEventListener("resize", this.scheduleViewportUpdate);
      this.visualViewport.removeEventListener("scroll", this.scheduleViewportUpdate);
      this.visualViewport = undefined;
    }
  }

  public componentDidUpdate(oldProps:IProps, oldState:IState) {
    if (
      oldProps.value !== this.props.value ||
      oldProps.style?.maxHeight !== this.props.style?.maxHeight ||
      oldState.viewportMaxHeight !== this.state.viewportMaxHeight
    ) {
      this.safeSyncHeight("update");
    }
  }

  public focus() {
    if (this.textAreaRef.current) {
      this.textAreaRef.current.focus();
    }
  }

  private onInternalChange(evt: React.ChangeEvent<HTMLTextAreaElement>) {
    this.safeSyncHeight("input", evt.currentTarget);
    this.props.onChange(evt);
  }

  private safeSyncHeight(stage:string, textarea?:HTMLTextAreaElement) {
    try {
      this.syncHeight(textarea);
    } catch (error) {
      this.reportViewportError(error, `height-${stage}`);
    }
  }

  private syncHeight(textarea?: HTMLTextAreaElement) {
    const element = textarea || this.textAreaRef.current;
    if (!element) {
      return;
    }

    element.style.height = "auto";

    const maxHeight = this.getMaxHeightPx();
    const scrollHeight = element.scrollHeight;

    if (maxHeight !== undefined) {
      element.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
      element.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
      return;
    }

    element.style.height = `${scrollHeight}px`;
    element.style.overflowY = 'hidden';
  }

  private getMaxHeightPx(): number | undefined {
    const configuredMaxHeight = this.getConfiguredMaxHeightPx();
    const { viewportMaxHeight } = this.state;

    if (configuredMaxHeight === undefined) {
      return viewportMaxHeight;
    }
    if (viewportMaxHeight === undefined) {
      return configuredMaxHeight;
    }
    return Math.min(configuredMaxHeight, viewportMaxHeight);
  }

  private getConfiguredMaxHeightPx(): number | undefined {
    const maxHeight = this.props.style?.maxHeight;
    if (typeof maxHeight === 'number') {
      const numericMaxHeight = maxHeight as number;
      return Number.isFinite(numericMaxHeight) ? numericMaxHeight : undefined;
    }

    if (typeof maxHeight === 'string') {
      const parsedMaxHeight = Number.parseFloat(maxHeight as string);
      return Number.isFinite(parsedMaxHeight) ? parsedMaxHeight : undefined;
    }

    const textarea = this.textAreaRef.current;
    if (!textarea) {
      return;
    }

    const computedMaxHeight = Number.parseFloat(window.getComputedStyle(textarea).maxHeight || '');
    return Number.isFinite(computedMaxHeight) ? computedMaxHeight : undefined;
  }

  // The height the visible viewport can spare for the input once the keyboard is
  // up. Only constrains the input on short screens; tall screens keep the
  // configured max via the Math.min in getMaxHeightPx.
  private updateViewportMaxHeight = () => {
    if (!this.lifecycleMounted) {
      return;
    }

    const visualViewport = this.visualViewport;
    const viewportHeight = visualViewport ? visualViewport.height : window.innerHeight;
    if (!Number.isFinite(viewportHeight) || viewportHeight <= 0) {
      return;
    }

    const available = Math.max(
      MIN_VIEWPORT_MAX_HEIGHT_PX,
      Math.round(viewportHeight - VIEWPORT_CHROME_RESERVE_PX),
    );

    if (available !== this.state.viewportMaxHeight) {
      this.setState({ viewportMaxHeight: available });
    }
  };

  // WebKit may emit several visualViewport resize/scroll events while it is in
  // the middle of updating the keyboard geometry. Coalesce those events and do
  // React/layout work on the next frame instead of re-entering synchronously.
  private scheduleViewportUpdate = () => {
    if (!this.lifecycleMounted || this.viewportUpdateFrame !== undefined) {
      return;
    }

    this.viewportUpdateFrame = window.requestAnimationFrame(() => {
      this.viewportUpdateFrame = undefined;
      if (!this.lifecycleMounted) {
        return;
      }

      try {
        this.updateViewportMaxHeight();
      } catch (error) {
        this.reportViewportError(error, "viewport-animation-frame");
      }
    });
  };

  private reportViewportError(error:unknown, stage:string) {
    try {
      (window as any).reportHandledError?.("Autosize viewport lifecycle", error, {
        stage,
        inputFocused:document.activeElement === this.textAreaRef.current,
      });
    } catch (reportError) {
      console.warn("Failed to report autosize viewport error:", reportError);
    }
  }

  private getVisualViewport(): IVisualViewport | undefined {
    return (window as Window & { visualViewport?: IVisualViewport }).visualViewport;
  }

}


export default Autosize;



// WEBPACK FOOTER //
// ./src/components/chat/Autosize.tsx